import { create } from "zustand";
import { financialValuesUnavailable, mergePositionRowsForGrid, positionRowDedupeKey } from "../lib/positionSnapshotEnvelope";
import type { SnapshotPosition } from "../types/positions";
import { useDashboardDisplayStore } from "./dashboardDisplayStore";

/**
 * Static metadata fields populated by BFF enrichment (back_office.instrument, book, entity
 * lookups). These are absent from the WS broadcaster path which sends raw accumulator data.
 * When a WS snapshot arrives and sends null for these fields, preserve the previously
 * REST-enriched value so the grid never reverts to showing empty cells.
 */
const ENRICHED_FIELDS: ReadonlyArray<keyof SnapshotPosition> = [
  "instrument_family",
  "instrument_kind",
  "symbol",
  "venue_symbol",
  "primary_venue",
  "entity_name",
  "book_name",
  "denomination_ccy",
  "mark_currency",
  "functional_ccy",
  "isin",
  "underlier_symbol",
  "underlier_instrument_id",
  "underlier_mark_price",
  "contract_multiplier",
  "contract_size_snap",
  // E5: manual price model — preserved from REST enrichment so WS deltas don't clear them
  "pricing_mode",
  "manual_price",
  "manual_price_currency",
  "manual_price_set_by",
  "manual_price_reason",
  "cash_delta",
  "cash_gamma",
  "cash_vega",
  "cash_rho",
  "cash_theta",
  "cash_fx_delta",
  "rho",
  "volga",
  "vanna",
  "pv01_functional",
  "dv01_functional",
  "cs01_functional",
  "yield_to_maturity",
  "asw_spread_bps",
  "yield_to_worst",
  "duration_modified",
  "duration_macaulay",
  "convexity",
  "z_spread_bps",
  "g_spread_bps",
  "clean_price",
  "dirty_price",
  "krd_3m_functional",
  "krd_6m_functional",
  "krd_1y_functional",
  "krd_2y_functional",
  "krd_3y_functional",
  "krd_5y_functional",
  "krd_7y_functional",
  "krd_10y_functional",
  "krd_20y_functional",
  "krd_30y_functional",
  "workout_date",
  "workout_kind",
  "invested_capital_functional",
  "holding_days",
  // C1/D1: first-class option economics + valuation provenance. These are
  // populated by the REST merge-cache path (instrument join); preserve them
  // across WS deltas so a partial WS tick doesn't blank them. The WS
  // broadcaster does not populate these today.
  "expiration_date",
  "strike_price",
  "option_type",
  "valuation_origin",
  "valuation_as_of",
  "market_exposure_functional",
  "funding_notional_functional",
  "accrued_interest_functional",
  "clean_market_value_functional",
  "dirty_market_value_functional",
  "outstanding_notional",
  "face_amount",
  "unrealized_pnl_functional",
  "realized_pnl_functional_day",
  "total_pnl_functional",
  "delta_pnl_functional",
  "gamma_pnl_functional",
  "vega_pnl_functional",
  "theta_pnl_functional",
  "carry_pnl_functional",
  "dv01_pnl_functional",
  "accrued_pnl_functional",
  "vanna_pnl_functional",
  "volga_pnl_functional",
  "residual_pnl_functional",
  "attribution_coverage",
];

/**
 * Merge an incoming (WS or REST) position row with the previously stored row, preserving
 * non-null enriched-metadata values from the existing row when the incoming row sends null.
 * Live numeric fields (quantity, pnl, marks, greeks) are always taken from `next`.
 */
function mergePreservingEnriched(
  prev: SnapshotPosition,
  next: SnapshotPosition,
): SnapshotPosition {
  const merged = mergePositionRowsForGrid([prev], [next])[0];
  const unavailable = financialValuesUnavailable(merged);
  for (const field of ENRICHED_FIELDS) {
    if (unavailable && (field.includes("functional") || field.startsWith("cash_") || field === "attribution_coverage")) continue;
    if (merged[field] == null && prev[field] != null) {
      // Intentional dynamic field assignment across ~100 optional fields; the
      // cast is local and bounded to this one assignment.
      // biome-ignore lint/suspicious/noExplicitAny: intentional dynamic field assignment
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (merged as Record<string, any>)[field] = prev[field];
    }
  }
  // recon_status: preserve the last known status when the incoming delta doesn't set it.
  // A recon break delta from pks-bff will set recon_status to "BREAK" or "OK"; a regular
  // accumulator delta that doesn't set it should not clear an existing status.
  if (merged.recon_status == null && prev.recon_status != null) {
    merged.recon_status = prev.recon_status;
  }
  return merged;
}

/** One-shot mutation for AG Grid to consume (avoid full-rowData churn). */
export type PositionsMutation =
  | { kind: "snapshot"; rows: SnapshotPosition[] }
  | {
      kind: "delta";
      upserts: SnapshotPosition[];
      removedKeys: string[];
    };

/** Currency a row's functional amounts are in (the BFF relabels it on conversion). */
function amountCurrency(row: SnapshotPosition): string {
  return (row.functional_ccy ?? "").trim().toUpperCase();
}

/**
 * A row converted for a display currency other than the selected one is a
 * frame from before the last switch (a late REST response or an in-flight WS
 * delta). Applying it put the old currency back on screen until the next
 * frame, which is the blink this guards against. Rows without the marker come
 * from a BFF that does not convert and are taken as they are.
 */
function admitsDisplayCcy(row: SnapshotPosition): boolean {
  const marked = row.extensions?.display_currency;
  if (typeof marked !== "string" || !marked.trim()) return true;
  const selected = useDashboardDisplayStore.getState().displayCcy;
  return marked.trim().toUpperCase() === selected.trim().toUpperCase();
}

function isMoneyField(key: string): boolean {
  if (key.endsWith("_currency")) return false;
  return key.includes("_functional") || key.startsWith("cash_") || key === "vega" || key === "theta";
}

/** `row` without its functional amounts, so none can be carried into another currency. */
function withoutMoneyFields(row: SnapshotPosition): SnapshotPosition {
  const out: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row)) {
    if (!isMoneyField(key)) out[key] = value;
  }
  const extensions: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(row.extensions ?? {})) {
    if (!isMoneyField(key)) extensions[key] = value;
  }
  out.extensions = extensions;
  return out as unknown as SnapshotPosition;
}

function mergeIncomingRow(prev: SnapshotPosition | undefined, incoming: SnapshotPosition): SnapshotPosition {
  if (!prev) return incoming;
  // Never fill a gap in one currency with an amount from another: metadata
  // still carries over, money fields come from the incoming row only.
  const prevCcy = amountCurrency(prev);
  const nextCcy = amountCurrency(incoming);
  const base = prevCcy && nextCcy && prevCcy !== nextCcy ? withoutMoneyFields(prev) : prev;
  return mergePreservingEnriched(base, incoming);
}

/** Merge snapshots by observed time, preserving omitted metadata and explicit unavailable values. */
export function mergeRestSnapshot(rows: SnapshotPosition[]): void {
  const store = usePositionsStore.getState();
  const existing = store.positions;
  const m = new Map<string, SnapshotPosition>();

  const admitted = rows.filter(admitsDisplayCcy);
  // A whole image for a currency no longer selected: keep what is shown.
  if (rows.length && !admitted.length) return;
  for (const r of admitted) {
    const key = positionRowDedupeKey(r);
    const prev = existing.get(key);
    m.set(key, mergeIncomingRow(prev, r));
  }

  const ts = admitted[0]?.snapshot_ts ?? store.snapshotTs;
  usePositionsStore.setState({
    positions: m,
    hasSnapshot: true,
    qualityError: null,
    snapshotTs: ts ?? null,
    mutation: { kind: "snapshot", rows: Array.from(m.values()) },
  });
}

interface PositionsState {
  qualityError: string | null;
  positions: Map<string, SnapshotPosition>;
  hasSnapshot: boolean;
  lastSeq: number;
  snapshotTs: string | null;
  mutation: PositionsMutation | null;
  setSnapshot: (rows: SnapshotPosition[]) => void;
  mergeRestSnapshot: (rows: SnapshotPosition[]) => void;
  applyDelta: (upserts: SnapshotPosition[], removedKeys: string[]) => void;
  applyPeriodPnlRefresh: (patches: Array<Record<string, string | null | undefined>>) => void;
  clearMutation: () => void;
  clear: () => void;
  /** Drop rows the predicate rejects (book scope narrowed); keeps the rest as-is. */
  retainWhere: (keep: (row: SnapshotPosition) => boolean) => void;
}

function deleteRemovedKeys(next: Map<string, SnapshotPosition>, removedKeys: string[]) {
  for (const k of removedKeys) {
    if (next.delete(k)) continue;
    const toDelete: string[] = [];
    for (const [mk, row] of next) {
      if (row.position_line_key === k) toDelete.push(mk);
    }
    for (const mk of toDelete) next.delete(mk);
  }
}

export const usePositionsStore = create<PositionsState>((set, get) => ({
  qualityError: null,
  positions: new Map(),
  hasSnapshot: false,
  lastSeq: 0,
  snapshotTs: null,
  mutation: null,
  setSnapshot: (incoming) => {
    const rows = incoming.filter(admitsDisplayCcy);
    if (incoming.length && !rows.length) return;
    const existing = get().positions;
    const m = new Map<string, SnapshotPosition>();
    for (const r of rows) {
      const key = positionRowDedupeKey(r);
      const prev = existing.get(key);
      m.set(key, mergeIncomingRow(prev, r));
    }
    const mergedRows = Array.from(m.values());
    const ts = mergedRows[0]?.snapshot_ts ?? get().snapshotTs;
    set({
      positions: m,
      hasSnapshot: true,
      qualityError: null,
      snapshotTs: ts ?? null,
      mutation: { kind: "snapshot", rows: mergedRows },
    });
  },
  mergeRestSnapshot: (rows) => mergeRestSnapshot(rows),
  applyDelta: (incoming, removedKeys) => {
    const upserts = incoming.filter(admitsDisplayCcy);
    if (!upserts.length && !removedKeys.length) return;
    const next = new Map(get().positions);
    deleteRemovedKeys(next, removedKeys);
    for (const r of upserts) {
      const key = positionRowDedupeKey(r);
      const prev = next.get(key);
      next.set(key, mergeIncomingRow(prev, r));
    }
    const ts = upserts[0]?.snapshot_ts ?? get().snapshotTs;
    set({
      positions: next,
      snapshotTs: ts ?? get().snapshotTs,
      mutation: { kind: "delta", upserts, removedKeys },
    });
  },
  applyPeriodPnlRefresh: (patches) => {
    if (!patches.length) return;
    const next = new Map(get().positions);
    const upserts: SnapshotPosition[] = [];
    for (const patch of patches) {
      const lineKey = patch.position_line_key;
      if (!lineKey) continue;
      let foundKey: string | undefined;
      let row: SnapshotPosition | undefined;
      for (const [k, r] of next) {
        if (r.position_line_key === lineKey) {
          foundKey = k;
          row = r;
          break;
        }
      }
      if (!row || !foundKey || financialValuesUnavailable(row)) continue;
      const updated: SnapshotPosition = {
        ...row,
        pnl_functional_1d: patch.pnl_functional_1d !== undefined ? patch.pnl_functional_1d : row.pnl_functional_1d,
        delta_pnl_functional_1d: patch.delta_pnl_functional_1d !== undefined ? patch.delta_pnl_functional_1d : row.delta_pnl_functional_1d,
        pnl_functional_since_open: patch.pnl_functional_since_open !== undefined ? patch.pnl_functional_since_open : row.pnl_functional_since_open,
        pnl_functional_1w: patch.pnl_functional_1w !== undefined ? patch.pnl_functional_1w : row.pnl_functional_1w,
        pnl_functional_1m: patch.pnl_functional_1m !== undefined ? patch.pnl_functional_1m : row.pnl_functional_1m,
        pnl_functional_ytd: patch.pnl_functional_ytd !== undefined ? patch.pnl_functional_ytd : row.pnl_functional_ytd,
        pnl_functional_1y: patch.pnl_functional_1y !== undefined ? patch.pnl_functional_1y : row.pnl_functional_1y,
        pnl_functional_itd: patch.pnl_functional_itd !== undefined ? patch.pnl_functional_itd : row.pnl_functional_itd,
      };
      next.set(foundKey, updated);
      upserts.push(updated);
    }
    if (!upserts.length) return;
    set({
      positions: next,
      mutation: { kind: "delta", upserts, removedKeys: [] },
    });
  },
  clearMutation: () => set({ mutation: null }),
  retainWhere: (keep) => {
    const current = get().positions;
    const removedKeys: string[] = [];
    const next = new Map<string, SnapshotPosition>();
    for (const [key, row] of current) {
      if (keep(row)) next.set(key, row);
      else removedKeys.push(key);
    }
    if (!removedKeys.length) return;
    set({ positions: next, mutation: { kind: "delta", upserts: [], removedKeys } });
  },
  clear: () => set({ positions: new Map(), hasSnapshot: false, snapshotTs: null, mutation: null }),
}));

export function selectPositionsArray(state: PositionsState): SnapshotPosition[] {
  return Array.from(state.positions.values());
}
